/**
 * Properties Management API Hooks
 * React Query hooks for property CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import AxiosClient from '@/api/client';
import { Endpoints, EndpointHelpers } from '@/config/endpoints';
import { DEFAULT_PAGE_SIZE } from '@/config/constants';
import { toast } from '@/hooks/use-toast';
import type {
  ApiProperty,
  PropertyDetailResponse,
  CreatePropertyRequest,
  CreatePropertyWithAdminRequest,
  UpdatePropertyRequest,
  PropertiesListParams,
  ItemResponse,
  ListPaginated,
} from '@/types/api';

// ============================================================================
// Query Keys
// ============================================================================

export const propertyQueryKeys = {
  all: ['properties'] as const,
  lists: () => [...propertyQueryKeys.all, 'list'] as const,
  list: (params: PropertiesListParams) =>
    [...propertyQueryKeys.lists(), params] as const,
  details: () => [...propertyQueryKeys.all, 'detail'] as const,
  detail: (id: string | number) =>
    [...propertyQueryKeys.details(), id] as const,
} as const;

// ============================================================================
// Properties List Query (Paginated)
// ============================================================================

export function usePropertiesQuery(params: PropertiesListParams = {}) {
  return useQuery({
    queryKey: propertyQueryKeys.list(params),
    queryFn: async (): Promise<ListPaginated<ApiProperty>> => {
      const client = new AxiosClient<undefined, ApiProperty>({
        endpoint: Endpoints.PROPERTIES_LIST,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getPaginated({
        params: {
          page: params.page || 1,
          page_size: params.page_size || DEFAULT_PAGE_SIZE,
          ...(params.search && { search: params.search }),
          ...(params.property_type && { property_type: params.property_type }),
          ...(params.status && { status: params.status }),
          ...(params.stars && { stars: params.stars }),
          ...(params.location && { location: params.location }),
        },
      });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch properties');
      }

      return response;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
}

// ============================================================================
// Property Detail Query
// ============================================================================

export function usePropertyQuery(propertyId: string | number) {
  return useQuery({
    queryKey: propertyQueryKeys.detail(propertyId),
    queryFn: async (): Promise<PropertyDetailResponse> => {
      const client = new AxiosClient<undefined, PropertyDetailResponse>({
        endpoint: EndpointHelpers.getPropertyDetail(propertyId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch property details');
      }

      return response.data;
    },
    enabled: !!propertyId, // Only run if propertyId is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// Create Property Mutation
// ============================================================================

interface UseCreatePropertyMutationOptions {
  onSuccessCallback?: () => void;
}

export function useCreatePropertyMutation(
  options: UseCreatePropertyMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      propertyData: CreatePropertyRequest | CreatePropertyWithAdminRequest
    ): Promise<ItemResponse<PropertyDetailResponse>> => {
      const client = new AxiosClient<
        CreatePropertyRequest | CreatePropertyWithAdminRequest,
        PropertyDetailResponse
      >({
        endpoint: Endpoints.PROPERTY_CREATE,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.post(propertyData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to create property');
      }

      return response;
    },

    onSuccess: response => {
      toast({
        title: 'Property Created',
        description: `${response.data.name} has been successfully created.`,
      });

      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: propertyQueryKeys.all });

      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Create property error:', error);

      let errorMessage = 'Failed to create property. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          // Handle validation errors
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Creation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Update Property Mutation
// ============================================================================

interface UseUpdatePropertyMutationOptions {
  onSuccessCallback?: () => void;
}

export function useUpdatePropertyMutation(
  propertyId: string | number,
  options: UseUpdatePropertyMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      propertyData: UpdatePropertyRequest
    ): Promise<ItemResponse<PropertyDetailResponse>> => {
      // Convert to FormData for file upload support
      const formData = new FormData();

      // Add _method field for Laravel method spoofing (PUT via POST)
      formData.append('_method', 'PUT');

      // Add all fields to FormData
      Object.entries(propertyData).forEach(([key, value]) => {
        if (key === 'property_scope' && Array.isArray(value)) {
          // Send property_scope as JSON string
          formData.append('property_scope', JSON.stringify(value));
        } else if (key === 'photo_base64' && value) {
          // Handle base64 image - convert to blob
          if (typeof value === 'string' && value.startsWith('data:image')) {
            const base64Data = value.split(',')[1];
            const mimeMatch = value.match(/data:(image\/\w+);base64,/);
            const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });
            const extension = mimeType.split('/')[1];
            formData.append('photo', blob, `property_photo.${extension}`);
          }
        } else if (value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });

      const client = new AxiosClient<FormData, PropertyDetailResponse>({
        endpoint: EndpointHelpers.updateProperty(propertyId),
        contentType: 'multipart/form-data',
        isToken: true,
      });

      // Use POST instead of PUT for multipart/form-data
      const response = await client.post(formData);

      if (!response.status) {
        // Create a custom error object that includes the response data
        const error = new Error(response.error || 'Failed to update property') as Error & {
          response?: { data: ItemResponse<unknown> };
        };
        error.response = { data: response as ItemResponse<unknown> };
        throw error;
      }

      return response;
    },

    onSuccess: response => {
      toast({
        title: 'Property Updated',
        description: `${response.data.name} has been successfully updated.`,
      });

      // Only invalidate list queries - detail will be refetched when needed
      // Don't invalidate detail query to avoid extra GET request before navigation
      queryClient.invalidateQueries({ queryKey: propertyQueryKeys.lists() });

      onSuccessCallback?.();
    },

    onError: (error: Error & { response?: { data: ItemResponse<unknown> } }) => {
      console.error('Update property error:', error);

      let errorMessage = 'Failed to update property. Please try again.';
      let errorTitle = 'Update Failed';

      if (error.response?.data) {
        const errorData = error.response.data;

        if (typeof errorData.detail === 'object' && errorData.detail !== null) {
          // Handle validation errors - get the detailed message from contract_end field
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join('\n');
          
          // Use the main error as title if available
          if (errorData.error) {
            errorTitle = errorData.error;
          }
        } else {
          errorMessage = errorData.error || (typeof errorData.detail === 'string' ? errorData.detail : errorMessage);
        }
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Delete Property Mutation
// ============================================================================

interface UseDeletePropertyMutationOptions {
  onSuccessCallback?: () => void;
}

export function useDeletePropertyMutation(
  options: UseDeletePropertyMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      propertyId: string | number
    ): Promise<ItemResponse<string>> => {
      const client = new AxiosClient<undefined, string>({
        endpoint: EndpointHelpers.deleteProperty(propertyId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.delete();

      if (!response.status) {
        throw new Error(response.error || 'Failed to delete property');
      }

      return response;
    },

    onSuccess: (response, propertyId) => {
      toast({
        title: 'Property Deleted',
        description: 'Property has been successfully deleted.',
      });

      // Remove from cache and refetch
      queryClient.invalidateQueries({ queryKey: propertyQueryKeys.all });
      queryClient.removeQueries({
        queryKey: propertyQueryKeys.detail(propertyId),
      });

      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Delete property error:', error);

      let errorMessage = 'Failed to delete property. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;
        errorMessage = errorData.error || errorData.detail || errorMessage;
      }

      toast({
        title: 'Delete Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get properties list from cache without triggering a network request
 */
export function usePropertiesCache(params: PropertiesListParams = {}) {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<ListPaginated<ApiProperty>>(
    propertyQueryKeys.list(params)
  );
}

/**
 * Prefetch property detail for better UX
 */
export function usePrefetchProperty() {
  const queryClient = useQueryClient();

  return (propertyId: string | number) => {
    queryClient.prefetchQuery({
      queryKey: propertyQueryKeys.detail(propertyId),
      queryFn: async (): Promise<PropertyDetailResponse> => {
        const client = new AxiosClient<undefined, PropertyDetailResponse>({
          endpoint: EndpointHelpers.getPropertyDetail(propertyId),
          contentType: 'application/json',
          isToken: true,
        });

        const response = await client.getItem();

        if (!response.status) {
          throw new Error(response.error || 'Failed to fetch property details');
        }

        return response.data;
      },
      staleTime: 5 * 60 * 1000,
    });
  };
}
